import React from "react";
import { AiOutlineEdit, AiOutlineDelete } from "react-icons/ai";
import { timestampToDateString } from "../../config/firebase/index";

const TaskDetails = ({
  task,
  editTaskClicked,
  isTemplate = false,
  deleteTaskClicked,
}) => {
  // Define color codes for different task types
  const typeColors = {
    pdf: "text-red-800 bg-red-100 dark:bg-red-900 dark:text-red-300",
    image:
      "text-orange-800 bg-orange-100 dark:bg-orange-900 dark:text-orange-300",
    text: "text-yellow-800 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300",
    conference:
      "text-purple-800 bg-purple-100 dark:bg-purple-900 dark:text-purple-300",
  };

  // Define color codes for different task statuses
  const statusColors = {
    accepted:
      "text-green-800 bg-green-100 dark:bg-green-900 dark:text-green-300",
    rejected: "text-red-800 bg-red-100 dark:bg-red-900 dark:text-red-300",
    reviewing:
      "text-yellow-800 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300",
    awaiting_submission:
      "text-blue-800 bg-blue-100 dark:bg-blue-900 dark:text-blue-300",
    closed: "text-gray-800 bg-gray-100 dark:bg-gray-900 dark:text-gray-300",
  };

  return (
    <div className="p-4 rounded-lg bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
      {/* Title and Edit/Delete Buttons */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
          {task.title}
        </h2>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => editTaskClicked(task)}
            className="text-gray-500 hover:text-purple-600 focus:outline-none"
          >
            <AiOutlineEdit className="text-xl" />
          </button>
          {isTemplate && (
            <button
              onClick={() => deleteTaskClicked(task)}
              className="text-gray-500 hover:text-red-600 focus:outline-none"
            >
              <AiOutlineDelete className="text-xl" />
            </button>
          )}
        </div>
      </div>
      <hr />

      {/* Description */}
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 mt-4">
        <strong>Description:</strong> {task.description}
      </p>

      {/* Type */}
      <div className="flex items-center space-x-2 mb-4">
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
          <strong>Type:</strong>
        </span>
        <span
          className={`px-2 py-1 text-xs font-semibold rounded ${
            typeColors[task.type] ||
            "text-gray-800 bg-gray-100 dark:bg-gray-900 dark:text-gray-300"
          }`}
        >
          {task.type?.toUpperCase()}
        </span>
      </div>

      {/* Status */}
      {task.status && (
        <div className="flex items-center space-x-2 mb-4">
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
            <strong>Status:</strong>
          </span>
          <span
            className={`px-2 py-1 text-xs font-semibold rounded ${
              statusColors[task.status] ||
              "text-gray-800 bg-gray-100 dark:bg-gray-900 dark:text-gray-300"
            }`}
          >
            {task.status.replace("_", " ").toUpperCase()}
          </span>
        </div>
      )}

      {/* Created At */}
      {isTemplate === false && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          <strong>Created At:</strong>{" "}
          {timestampToDateString(task.createdAt) || "N/A"}
        </p>
      )}

      {/* Deadline */}
      {isTemplate === false && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          <strong>Deadline:</strong>{" "}
          {task.deadline ? (
            <span className="text-red-600 dark:text-red">
              {timestampToDateString(task.deadline)}
            </span>
          ) : (
            "No deadline set"
          )}
        </p>
      )}

      {/* Attachment */}
      {task.attachment && (
        <div className="mb-4">
          <strong className="text-sm text-gray-600 dark:text-gray-400">
            Attachment:{" "}
          </strong>
          <a
            href={task.attachment}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-500 hover:underline dark:text-blue-400"
          >
            View Attachment
          </a>
        </div>
      )}

      {/* Comment */}
      <p className="text-sm text-gray-600 dark:text-gray-400">
        <strong>Comment:</strong> {task.comment || "No comment"}
      </p>
      {!isTemplate && (
        <div className="mt-4">
          <hr className="border-t border-dotted border-gray-200 dark:border-gray-600" />
          {/* Submitted Document */}
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
            <strong>Document:</strong>{" "}
            {task.document ? (
              task.document.startsWith("http") ? ( // Check if it's a valid URL
                <a
                  href={task.document}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline dark:text-blue-400"
                >
                  Preview Document
                </a>
              ) : (
                task.document // Display as plain text if not a URL
              )
            ) : (
              "No document submitted"
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default TaskDetails;
